import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useSeoFormContext,
  FormData as SEOFormData,
} from '../Context/SeoFormContext';
import { API_ENDPOINTS } from '../../../api';
import { getService, postService } from '../../../servce/ApisService';
import { useToaster } from '../../../Toaster/Toaster';
import useSeoPageTypeFromPathname from '../../../Hooks/useSeoPageTypeFromPathname';
import { useLoader } from '../../../Loader/LoaderContext';
import ConfirmationModal from '../Modals/SEOConfirmationModal';

// Define FormErrors interface
interface FormErrors {
  seoMetaTitle?: string;
  seoDescription?: string;
  seoKeyphrase?: string;
}

const SeoSettings: React.FC = () => {
  const { showToast } = useToaster();
  const { setLoading } = useLoader();
  const seoPageType = useSeoPageTypeFromPathname();
  const { pathname } = useLocation();
  const { getForm, setForm, resetForm } = useSeoFormContext();
  const [hasExistingSeo, setHasExistingSeo] = useState<boolean>(false);
  const { id } = useParams();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('id');

  const existingData = getForm(pathname);

  const [formData, setFormData] = useState<SEOFormData>(
    existingData && (existingData.seoMetaTitle || existingData.seoDescription)
      ? existingData
      : {
          seoMetaTitle: '',
          seoDescription: '',
          seoKeyphrase: '',
          keywords: [],
          keyphrases: [],
        }
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [keywordsInput, setKeywordsInput] = useState<string>('');
  const [keyphraseInput, setKeyphraseInput] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(
    !existingData || !(existingData.seoMetaTitle || existingData.seoDescription)
  );
  const metaTitleRef = useRef<HTMLInputElement>(null);
  const metaDescriptionRef = useRef<HTMLTextAreaElement>(null);

  // Save form data in context when it changes
  useEffect(() => {
    setForm(pathname, formData);
  }, [formData]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const newError = validateField(name as keyof SEOFormData, value);
    setErrors((prev: FormErrors) => ({
      ...prev,
      [name]: newError,
    }));
  };

  const validateField = (name: keyof SEOFormData, value: string) => {
    let error: string | undefined;

    if (name === 'seoMetaTitle') {
      if (value.length < 10)
        error = 'Meta Title must be at least 10 characters';
      else if (value.length > 60)
        error = 'Meta Title must not exceed 60 characters';
    }

    if (name === 'seoDescription') {
      if (value.length < 50)
        error = 'Meta Description must be at least 50 characters';
      else if (value.length > 160)
        error = 'Meta Description must not exceed 160 characters';
    }

    return error;
  };

  const addKeyword = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newKeywords = keywordsInput
        .split(',')
        .map((k) => k.trim())
        .filter((k) => {
          // Check if keyword already exists (case insensitive)
          return (
            k &&
            !formData.keywords.some(
              (existing) => existing.toLowerCase() === k.toLowerCase()
            )
          );
        });

      if (newKeywords.length > 0) {
        setFormData((prev) => ({
          ...prev,
          keywords: [...prev.keywords, ...newKeywords],
        }));
        setKeywordsInput('');
      }
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter(
        (k) => k.toLowerCase() !== keyword.toLowerCase()
      ),
    }));
  };

  const addKeyphrase = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const phrase = keyphraseInput.trim();
      if (
        phrase &&
        !formData.keyphrases.some(
          (existing) => existing.toLowerCase() === phrase.toLowerCase()
        )
      ) {
        setFormData((prev) => ({
          ...prev,
          keyphrases: [...prev.keyphrases, phrase],
        }));
        setKeyphraseInput('');
      }
    }
  };

  const removeKeyphrase = (phrase: string) => {
    setFormData((prev) => ({
      ...prev,
      keyphrases: prev.keyphrases.filter(
        (p) => p.toLowerCase() !== phrase.toLowerCase()
      ),
    }));
  };
  const handleCancel = () => {
    const savedData = getForm(pathname);
    const hasRealContent =
      savedData && (savedData.seoMetaTitle || savedData.seoDescription);

    if (hasRealContent) {
      setFormData(savedData);
      setIsEditing(false);
    } else {
      setFormData({
        seoMetaTitle: '',
        seoDescription: '',
        seoKeyphrase: '',
        keywords: [],
        keyphrases: [],
      });
      setIsEditing(true); // Stay in edit mode if there was no real content
    }

    setKeywordsInput('');
    setKeyphraseInput('');
    setErrors({});
  };

  const handleSave = () => {
    const titleError = validateField('seoMetaTitle', formData.seoMetaTitle);
    const descError = validateField('seoDescription', formData.seoDescription);

    if (titleError || descError) {
      return;
    }

    // Open confirmation modal instead of saving directly
    setIsConfirmModalOpen(true);
  };

  const handleSaveConfirmed = async () => {
    setIsConfirmModalOpen(false); // Close modal first

    const payload = {
      id: sectionId,
      metaTitle: formData.seoMetaTitle,
      metaDescription: formData.seoDescription,
      keyWords: formData.keywords.join(','),
      keyPhrase: formData.keyphrases.join(','),
    };

    try {
      setLoading(true);

      const endpoint = API_ENDPOINTS.addTagSeoSettings;

      await postService(endpoint, payload);

      showToast(
        hasExistingSeo
          ? 'SEO data updated successfully!'
          : 'SEO data saved successfully!',
        'success',
        ''
      );

      setHasExistingSeo(true); // Mark it as existing from now on
      setIsEditing(false);
    } catch (err: unknown) {
      const error = err as any;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      showToast(`${message}`, 'error', '');
      console.error('Error saving/updating SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true); // Toggle editing state
  };

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        setLoading(true);
        const url = `${API_ENDPOINTS.getTagSeoSettings}?id=${sectionId}`;
        const res = await getService(url);

        if (res) {
          const data = res?.data;
          const hasRealContent =
            data?.metaTitle !== null ||
            data?.metaDescription !== null ||
            data?.keyWords !== null ||
            data?.keyPhrase !== null;

          setHasExistingSeo(hasRealContent); // Only set to true if we have real content

          const newFormData: SEOFormData = {
            seoMetaTitle: data?.metaTitle || '',
            seoDescription: data?.metaDescription || '',
            seoKeyphrase: data?.keyPhrase || '',
            keywords: data?.keyWords ? data?.keyWords.split(',') : [],
            keyphrases: data?.keyPhrase ? data?.keyPhrase.split(',') : [],
          };

          setFormData(newFormData);
          setForm(pathname, newFormData);
          setIsEditing(!hasRealContent); // Set to edit mode if no real content exists
        }
      } catch (err: any) {
        // Handle error
      } finally {
        setLoading(false);
      }
    };

    fetchSeoData();
  }, [pathname, sectionId]);

  const navigate = useNavigate();
  console.log(id);

  return (
    <div className='p-6'>
      <div>
        <button
          className='flex items-center gap-2'
          onClick={() => navigate(-1)}
        >
          <img src='/images/back.svg' />
          Back
        </button>
      </div>

      <h1 className='text-2xl font-bold mb-6 capitalize'>{id}</h1>
      <div className='bg-white rounded-lg shadow p-6'>
        <div className='mb-4'>
          <h2 className='text-xl font-semibold text-gray-800'>
            SEO Meta Details
          </h2>
          <p className='text-sm text-gray-500'>
            Fill in the SEO details for this page.
          </p>
        </div>

        {/* Meta Title */}
        <div className='mb-4'>
          <label className='block font-medium text-sm text-gray-700'>
            Meta Title
          </label>
          <input
            type='text'
            name='seoMetaTitle'
            value={formData.seoMetaTitle}
            onChange={handleInputChange}
            placeholder='Enter your meta title'
            className='w-full p-2 border rounded mt-1'
            ref={metaTitleRef}
            disabled={!isEditing} // Make field editable only when isEditing is true
          />
          {errors.seoMetaTitle && (
            <p className='text-sm text-red-600 mt-1'>{errors.seoMetaTitle}</p>
          )}
        </div>

        {/* Meta Description */}
        <div className='mb-4'>
          <label className='block font-medium text-sm text-gray-700'>
            Meta Description
          </label>
          <textarea
            name='seoDescription'
            value={formData.seoDescription}
            onChange={handleInputChange}
            rows={4}
            className='w-full p-2 border rounded mt-1'
            placeholder='Enter meta description'
            ref={metaDescriptionRef}
            disabled={!isEditing} // Make field editable only when isEditing is true
          />
          {errors.seoDescription && (
            <p className='text-sm text-red-600 mt-1'>{errors.seoDescription}</p>
          )}
        </div>

        {/* Keywords */}
        <div className='mb-4'>
          <label className='block pb-1 font-medium text-sm text-gray-700'>
            Keywords
          </label>
          <div className='flex flex-wrap gap-2 mb-2'>
            {formData.keywords.map((k, i) => (
              <span
                key={i}
                className='bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1'
              >
                {k}
                {isEditing && (
                  <button onClick={() => removeKeyword(k)} className='text-sm'>
                    &times;
                  </button>
                )}
              </span>
            ))}
          </div>

          <textarea
            rows={2}
            value={keywordsInput}
            onChange={(e) => setKeywordsInput(e.target.value)}
            onKeyDown={addKeyword}
            placeholder='Press Enter to add keyword'
            className='w-full p-2 border rounded mt-1'
            disabled={!isEditing} // Make field editable only when isEditing is true
          />
        </div>

        {/* Keyphrases */}
        <div className='mb-4'>
          <label className='block font-medium text-sm text-gray-700'>
            Keyphrases (optional)
          </label>
          <div className='flex flex-wrap gap-2 mb-2'>
            {formData.keyphrases.map((p, i) => (
              <span
                key={i}
                className='bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1'
              >
                {p}
                {isEditing && (
                  <button
                    onClick={() => {
                      removeKeyphrase(p);
                    }}
                    className='text-sm'
                  >
                    &times;
                  </button>
                )}
              </span>
            ))}
          </div>

          <input
            type='text'
            value={keyphraseInput}
            onChange={(e) => setKeyphraseInput(e.target.value)}
            onKeyDown={addKeyphrase}
            placeholder='Press Enter to add keyphrase'
            className='w-full p-2 border rounded mt-1'
            disabled={!isEditing} // Make field editable only when isEditing is true
          />
        </div>

        {/* Buttons */}
        <div className='flex justify-end gap-4'>
          {isEditing && (
            <button
              onClick={handleCancel}
              className='bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded transition-colors'
            >
              Cancel
            </button>
          )}

          <button
            onClick={isEditing ? handleSave : handleEdit}
            className='bg-primary text-white py-2 px-4 rounded'
          >
            {isEditing ? 'Save' : 'Edit'}
          </button>
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleSaveConfirmed}
          title='Confirm SEO Changes'
          message='Are you sure you want to save these SEO meta details? This will apply the changes to the selected page.'
        />
      </div>
    </div>
  );
};

export default SeoSettings;
